import { Logo } from '@apuestapr/ui';
import { useMemo, type ReactElement } from 'react';
import { Navigate, Route, RouterProvider, Routes, createBrowserRouter } from 'react-router-dom';

import type { ApiClient } from './api/client.ts';
import { AuthView, SessionActions } from './auth/AuthSection.tsx';
import { SessionProvider, useSessionContext } from './auth/SessionProvider.tsx';
import type { ImageUploader } from './content/upload.ts';
import { ContentRouteTree } from './content/routes.tsx';
import { DesignSystemPage, designRoutePath } from './design/DesignSystemPage.tsx';
import { DashboardHome } from './home/DashboardHome.tsx';
import { LocationsRouteTree } from './locations/routes.tsx';
import { AppShell } from './shell/AppShell.tsx';
import { canOpenSupport } from './support/permissions.ts';
import { SupportRouteTree } from './support/routes.tsx';
import { StaffRouteTree } from './staff/routes.tsx';
import { ThemeToggle } from './theme/ThemeToggle.tsx';
import { YubikeysRouteTree } from './yubikeys/routes.tsx';
import { BalancesPage } from './balances/BalancesPage.tsx';
import { DocumentsPage } from './documents/DocumentsPage.tsx';
import { AnalyticsPage } from './analytics/AnalyticsPage.tsx';
import { DemographicsPage } from './analytics/DemographicsPage.tsx';
import { OwnerReportPage } from './analytics/OwnerReportPage.tsx';
import { VipTiersPage } from './analytics/VipTiersPage.tsx';

/**
 * The unauthenticated shell — checking or anonymous. Deliberately simpler than `AppShell`: there is no
 * role to gate navigation by yet, so this is the logo and the sign-in form, centred, with nothing a
 * visitor cannot use.
 */
const SignInScreen = (): ReactElement => {
  const session = useSessionContext();
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4">
      <h1 className="m-0 flex items-center gap-3 font-display text-xl">
        <Logo width={160} />
        <span className="text-muted-foreground">· Panel</span>
      </h1>
      <AuthView
        state={session.state}
        logIn={session.logIn}
        logOut={session.logOut}
        refresh={session.refresh}
      />
    </div>
  );
};

const ForbiddenContent = (): ReactElement => (
  <section>
    <h2>Contenido no disponible</h2>
    <p role="alert">Tu rol no tiene permiso para administrar contenido.</p>
  </section>
);

const ForbiddenLocations = (): ReactElement => (
  <section>
    <h2>Localidades no disponibles</h2>
    <p role="alert">Tu rol no tiene permiso para administrar localidades.</p>
  </section>
);

const ForbiddenStaff = (): ReactElement => (
  <section>
    <h2>Personal no disponible</h2>
    <p role="alert">Solo un administrador puede administrar cuentas de personal.</p>
  </section>
);

/**
 * `showSupport` is separate from `showContent` on purpose — Task 13.1.
 *
 * The Content Console is editorial only, so an `agente` sees no link to it. Support is the opposite: every
 * authenticated staff role works the queue, and an agente who could not reach it would have no way to do the
 * job the role exists for.
 */
const AuthenticatedDashboard = (): ReactElement => {
  const session = useSessionContext();
  if (session.state.status !== 'authenticated') return <></>;
  const { user } = session.state.session;
  const editorial = user.rol !== 'agente';
  const support = canOpenSupport(user.rol);

  // Locations and YubiKeys share the same admin/manager bar as the Content Console: the location
  // detail route carries IP addresses, gaming licence numbers, and OTC PINs, and the pool screen
  // sits beside it rather than living behind a separate rule.
  const locations = editorial;

  // Staff management is admin only — the one power a manager must not have, because creating an
  // admin is the same as promoting yourself. This gate is a courtesy; the API's requireRole('admin')
  // is the control, so the route below is still mounted only for an admin session.
  const staff = user.rol === 'admin';

  return (
    <AppShell
      showContent={editorial}
      showSupport={support}
      showLocations={locations}
      showStaff={staff}
      staffRole={user.rol}
      userName={user.nombre}
      footer={
        <div className="flex items-center justify-between gap-2">
          <ThemeToggle />
          <SessionActions
            onRefresh={() => void session.refresh()}
            onLogOut={() => void session.logOut()}
          />
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<DashboardHome />} />
        <Route
          path="/content/*"
          element={editorial ? <ContentRouteTree /> : <ForbiddenContent />}
        />
        {/* Mounted for every role the matrix admits, so nothing is fetched behind a route a session
            cannot use. `canOpenSupport` is true for all three roles today; this branch is where that
            would be enforced if it ever narrowed. */}
        <Route path="/support/*" element={support ? <SupportRouteTree /> : <ForbiddenContent />} />
        <Route
          path="/locations/*"
          element={locations ? <LocationsRouteTree /> : <ForbiddenLocations />}
        />
        <Route
          path="/yubikeys/*"
          element={locations ? <YubikeysRouteTree /> : <ForbiddenLocations />}
        />
        <Route path="/balances" element={locations ? <BalancesPage /> : <ForbiddenLocations />} />
        <Route path="/documents" element={editorial ? <DocumentsPage /> : <ForbiddenContent />} />
        <Route path="/analytics" element={editorial ? <AnalyticsPage /> : <ForbiddenContent />} />
        <Route
          path="/demographics"
          element={editorial ? <DemographicsPage /> : <ForbiddenContent />}
        />
        <Route path="/vip-tiers" element={editorial ? <VipTiersPage /> : <ForbiddenContent />} />
        <Route path="/staff/*" element={staff ? <StaffRouteTree /> : <ForbiddenStaff />} />
        <Route path="/owner-report" element={staff ? <OwnerReportPage /> : <ForbiddenStaff />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
};

const Dashboard = (): ReactElement => {
  const session = useSessionContext();

  if (session.state.status !== 'authenticated') return <SignInScreen />;

  return <AuthenticatedDashboard />;
};

const RoutedApp = ({
  client,
  imageUploader,
}: {
  readonly client: ApiClient;
  readonly imageUploader?: ImageUploader | undefined;
}): ReactElement => {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '*',
          element: (
            <SessionProvider client={client} imageUploader={imageUploader}>
              <Dashboard />
            </SessionProvider>
          ),
        },
      ]),
    [client, imageUploader],
  );
  return <RouterProvider router={router} />;
};

export const App = ({
  client,
  imageUploader,
}: {
  readonly client: ApiClient;
  readonly imageUploader?: ImageUploader | undefined;
}): ReactElement => {
  /** Keep this direct build-time branch: production-build.test.ts proves the page is eliminated. */
  if (import.meta.env.DEV && globalThis.location.pathname === designRoutePath) {
    return <DesignSystemPage />;
  }

  return <RoutedApp client={client} imageUploader={imageUploader} />;
};
